import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function TopicCategoriesContent() {
  const { t, language } = useLanguage();
  const { isAdmin } = useAuth();
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ name: '', name_el: '' });

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/topic-categories`, { withCredentials: true });
      setCategories(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchCategories(); }, []);

  const openAdd = () => { setEditing(null); setForm({ name: '', name_el: '' }); setDialogOpen(true); };
  const openEdit = (cat) => { setEditing(cat); setForm({ name: cat.name, name_el: cat.name_el || '' }); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      if (editing) {
        await axios.put(`${API}/topic-categories/${editing.category_id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/topic-categories`, form, { withCredentials: true });
      }
      toast.success(t('savedSuccess'));
      setDialogOpen(false);
      fetchCategories();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/topic-categories/${deleting.category_id}`, { withCredentials: true });
      toast.success(t('deletedSuccess'));
      setDeleteOpen(false);
      fetchCategories();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  return (
    <div data-testid="topic-categories-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{t('topicCategories')}</h1>
        {isAdmin && (
          <Button onClick={openAdd} className="bg-zinc-950 rounded-none" data-testid="add-topic-category-btn">
            <Plus size={18} className="mr-2" />{t('addTopicCategory')}
          </Button>
        )}
      </div>

      {categories.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500" data-testid="no-categories">{t('noTopicCategories')}</div>
      ) : (
        <div className="bg-white border border-zinc-200 overflow-hidden">
          <table className="w-full text-sm" data-testid="topic-categories-table">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('nameEn')}</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('nameEl')}</th>
                {isAdmin && <th className="text-right px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider w-24">{t('actions')}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {categories.map(cat => (
                <tr key={cat.category_id} className="hover:bg-zinc-50 transition-colors" data-testid={`category-row-${cat.category_id}`}>
                  <td className="px-5 py-3 font-medium">{cat.name}</td>
                  <td className="px-5 py-3 text-zinc-600">{cat.name_el || '-'}</td>
                  {isAdmin && (
                    <td className="px-5 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(cat)} className="p-1.5 hover:bg-zinc-100 transition-colors" data-testid={`edit-cat-${cat.category_id}`}><PencilSimple size={16} /></button>
                        <button onClick={() => { setDeleting(cat); setDeleteOpen(true); }} className="p-1.5 hover:bg-red-50 text-red-600 transition-colors" data-testid={`delete-cat-${cat.category_id}`}><Trash size={16} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{editing ? t('editCategory') : t('addTopicCategory')}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>{t('nameEn')} *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-none" required data-testid="cat-name-en-input" /></div>
            <div><Label>{t('nameEl')}</Label><Input value={form.name_el} onChange={e => setForm({ ...form, name_el: e.target.value })} className="mt-1 rounded-none" data-testid="cat-name-el-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
              <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-category-btn">{t('save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>{t('deleteTopicCategory')}</AlertDialogTitle><AlertDialogDescription>{t('confirmDeleteCategory')}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 rounded-none" data-testid="confirm-delete-cat">{t('delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function TopicCategories() {
  return (
    <Layout>
      <TopicCategoriesContent />
    </Layout>
  );
}
